import React, { useEffect, useState } from 'react';
import { getProjectRoles } from '../api/roles';
import { getProjects } from '../api/projects';
import { ProjectRole } from '../types/role';
import { Project } from '../types/project';

interface UserRolesProps {
  userId: string;
}

const UserRoles: React.FC<UserRolesProps> = ({ userId }) => {
  const [roles, setRoles] = useState<ProjectRole[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadUserRoles = React.useCallback(async () => {
    try {
      setLoading(true);
      const [rolesData, projectsData] = await Promise.all([
        getProjectRoles(userId),
        getProjects()
      ]);
      setRoles(rolesData);
      setProjects(projectsData);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user roles';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUserRoles();
  }, [loadUserRoles]);

  const getProjectName = (projectId: string): string => {
    if (!projects || !Array.isArray(projects)) return 'Unknown Project';
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  if (error) {
    return <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100">{error}</div>;
  }

  if (roles.length === 0) {
    return (
      <div style={{ 
        padding: 'var(--spacing-lg)', 
        textAlign: 'center', 
        color: 'var(--gray-500)',
        fontStyle: 'italic'
      }}>
        No project roles assigned yet
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 'var(--spacing-md)', color: 'var(--gray-900)' }}>
        Project Roles
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {roles.map((role) => (
          <div
            key={role.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--spacing-md)',
              backgroundColor: 'var(--gray-50)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--gray-200)',
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: 'var(--gray-900)', marginBottom: 'var(--spacing-xs)' }}>
                {getProjectName(role.project_id)}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--primary-color)' }}>
                {role.role}
              </div>
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
              Assigned: {new Date(role.assigned_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserRoles;
